import { useEffect, useRef, useState } from "react";
import styles from "../style.module.css";

function loadYouTubeApi() {
    if (window.YT?.Player) {
        return Promise.resolve(window.YT);
    }
    if (window.__vlvYtApiPromise) {
        return window.__vlvYtApiPromise;
    }
    window.__vlvYtApiPromise = new Promise((resolve) => {
        const previous = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = () => {
            if (typeof previous === "function") previous();
            resolve(window.YT);
        };
        if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
            const script = document.createElement("script");
            script.src = "https://www.youtube.com/iframe_api";
            script.async = true;
            document.head.appendChild(script);
        }
    });
    return window.__vlvYtApiPromise;
}

export default function CustomVideo({ youtubeId }) {
    const hostRef = useRef(null);
    const playerRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);

    useEffect(() => {
        let cancelled = false;

        loadYouTubeApi().then((YT) => {
            if (cancelled || !hostRef.current || !youtubeId) return;

            if (playerRef.current?.loadVideoById) {
                playerRef.current.cueVideoById(youtubeId);
                setIsPlaying(false);
                return;
            }

            playerRef.current = new YT.Player(hostRef.current, {
                videoId: youtubeId,
                host: "https://www.youtube-nocookie.com",
                playerVars: {
                    autoplay: 0,
                    controls: 0,
                    disablekb: 1,
                    fs: 0,
                    iv_load_policy: 3,
                    modestbranding: 1,
                    playsinline: 1,
                    rel: 0,
                    showinfo: 0,
                    cc_load_policy: 0,
                    origin: window.location.origin,
                },
                events: {
                    onStateChange: (event) => {
                        const playing = event.data === YT.PlayerState.PLAYING;
                        setIsPlaying(playing);
                        if (event.data === YT.PlayerState.ENDED) {
                            setIsPlaying(false);
                        }
                    },
                },
            });
        });

        return () => {
            cancelled = true;
        };
    }, [youtubeId]);

    useEffect(() => {
        return () => {
            try {
                playerRef.current?.destroy?.();
            } catch (e) {
                // ignore
            }
            playerRef.current = null;
        };
    }, []);

    const togglePlay = () => {
        const player = playerRef.current;
        if (!player?.playVideo) return;
        if (isPlaying) {
            player.pauseVideo();
        } else {
            player.playVideo();
        }
    };

    const toggleMute = () => {
        const player = playerRef.current;
        if (!player?.mute) return;
        if (player.isMuted?.()) {
            player.unMute();
            setIsMuted(false);
        } else {
            player.mute();
            setIsMuted(true);
        }
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.video} ref={hostRef} />
            {!isPlaying ? (
                <img
                    className={styles.videoPoster}
                    src={`https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`}
                    alt=""
                />
            ) : null}
            <button
                type="button"
                className={styles.playButton}
                onClick={togglePlay}
                aria-label={isPlaying ? "Pause" : "Play"}
            >
                {isPlaying ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                        <rect x="6" y="5" width="4" height="14" rx="1" />
                        <rect x="14" y="5" width="4" height="14" rx="1" />
                    </svg>
                ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                        <path d="M8 5v14l11-7z" />
                    </svg>
                )}
            </button>
            <button
                type="button"
                className={styles.muteButton}
                onClick={toggleMute}
                aria-label={isMuted ? "Unmute" : "Mute"}
            >
                {isMuted ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                        <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63z" />
                        <path d="M19 12c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
                    </svg>
                ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                        <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
                    </svg>
                )}
            </button>
        </div>
    );
}
